import { useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { motion, useMotionValue, useTransform, useSpring, useReducedMotion, animate } from "framer-motion";
import Letter from "./Letter";
import LetterKey, { keyViewWidth, keyViewHeight } from "./LetterKey";
import useOrientation from "./useOrientation";
import "./envelope.css";

const emergeScale = 0.86;
const openAngle = 180;
const tearLiftAngle = 10;
const notchShare = 0.8;
const emergeClipLead = 2;
const viewportMargin = 10;

const heartMask =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='M12 20.5C9 18.7 4.4 15.3 2.6 11.5 1 8.1 3.1 4.4 6.8 4.4c2.2 0 3.9 1.2 5.2 3 1.3-1.8 3-3 5.2-3 3.7 0 5.8 3.7 4.2 7.1-1.8 3.8-6.4 7.2-9.4 9z'/></svg>\")";

const fleckColors = ["#fffaf5", "#fffaf5", "#f9d5dd", "#f4b6c2", "#fde9ee"];

const foldGeometry = {
  landscape: {
    viewBox: "0 0 100 77.3",
    topPath: "M0 0 L50 44 L100 0",
    bottomPath: "M0 77.3 L50 44 L100 77.3",
    rect: { x: 0.3, y: 0.3, width: 99.4, height: 76.7 },
  },
  portrait: {
    viewBox: "0 0 77.3 100",
    topPath: "M0 0 L38.65 52 L77.3 0",
    bottomPath: "M0 100 L38.65 52 L77.3 100",
    rect: { x: 0.3, y: 0.3, width: 76.7, height: 99.4 },
  },
};

const seededRandom = (seed) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

const buildTearGeometry = (width, cutY) => {
  const random = seededRandom(29);
  const points = [[0, cutY]];
  let x = 0;
  let side = 1;
  while (x < width) {
    x = Math.min(width, x + 2.4 + random() * 4.4);
    side = -side;
    points.push([x, cutY + side * (0.45 + random() * 1.5)]);
  }
  const toPath = (shift) =>
    points.map(([px, py], index) => `${index ? "L" : "M"}${px.toFixed(1)} ${(py + shift).toFixed(1)}`).join(" ");
  let fibers = "";
  points.forEach(([px, py]) => {
    if (random() < 0.5) {
      const startY = py + 1.3;
      fibers += `M${px.toFixed(1)} ${startY.toFixed(1)} L${(px + (random() - 0.5) * 2.2).toFixed(1)} ${(startY + 0.9 + random() * 2).toFixed(1)} `;
    }
    if (random() < 0.3) {
      const startY = py - 1.2;
      fibers += `M${px.toFixed(1)} ${startY.toFixed(1)} L${(px + (random() - 0.5) * 2).toFixed(1)} ${(startY - 0.7 - random() * 1.5).toFixed(1)} `;
    }
  });
  const upperReversed = [...points]
    .reverse()
    .map(([px, py]) => `L${px.toFixed(1)} ${(py - 1.1).toFixed(1)}`)
    .join(" ");
  return {
    line: toPath(0),
    upper: toPath(-1.1),
    lower: toPath(1.25),
    shadow: toPath(2.8),
    fibers,
    strip: `M0 0 L${width.toFixed(1)} 0 ${upperReversed} Z`,
  };
};

export default function Envelope({ backImageSrc, backImageSrcPortrait, letterText, onPhaseChange }) {
  const isPortrait = useOrientation();
  const prefersReducedMotion = useReducedMotion();
  const fold = isPortrait ? foldGeometry.portrait : foldGeometry.landscape;
  const resolvedBackImage = isPortrait && backImageSrcPortrait ? backImageSrcPortrait : backImageSrc;
  const [state, setState] = useState({
    phase: "back",
    showEmerge: false,
    showReading: false,
    frontArmed: false,
  });
  const { phase, showEmerge, showReading, frontArmed } = state;
  const [isCutting, setIsCutting] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (onPhaseChange) onPhaseChange(phase);
  }, [phase, onPhaseChange]);

  const wrapRef = useRef(null);
  const emergePaperRef = useRef(null);
  const readingPaperRef = useRef(null);
  const fleckLayerRef = useRef(null);
  const transitioningRef = useRef(false);
  const emergeEndRectRef = useRef(null);
  const poseRef = useRef(null);
  const strokeStartRef = useRef(0);
  const lastFleckProgressRef = useRef(0);
  const geometryRef = useRef(null);

  const tearProgress = useMotionValue(0);
  const flapRotate = useMotionValue(0);
  const sealOpacity = useMotionValue(1);
  const sealPop = useMotionValue(1);
  const emergeY = useMotionValue(0);
  const sinkY = useMotionValue(0);
  const settleScaleX = useMotionValue(1);
  const settleScaleY = useMotionValue(1);
  const readingX = useMotionValue(0);
  const readingY = useMotionValue(0);
  const readingScaleX = useMotionValue(1);
  const readingScaleY = useMotionValue(1);
  const readingTilt = useMotionValue(0);
  const keyExit = useMotionValue(0);
  const keyLean = useMotionValue(0);
  const keyLeanSpring = useSpring(keyLean, { stiffness: 240, damping: 24 });

  const tearClip = useTransform(tearProgress, (p) => `inset(-4px ${((1 - p) * 100).toFixed(2)}% -4px 0)`);
  const tearOverlayOpacity = useTransform(flapRotate, [40, 110], [1, 0]);
  const keyCarriageX = useTransform(tearProgress, (p) => `${(p * 100).toFixed(3)}%`);
  const keyBaseAngle = useTransform(tearProgress, [0, 0.7, 1], [-36, -42, -74]);
  const keyAngle = useTransform([keyBaseAngle, keyLeanSpring], ([base, lean]) => base + lean);
  const keyExitX = useTransform(keyExit, [0, 1], [0, 44]);
  const keyExitY = useTransform(keyExit, [0, 1], [0, -34]);
  const keyExitRotate = useTransform(keyExit, [0, 1], [0, 16]);
  const keyOpacity = useTransform(keyExit, [0, 0.85], [1, 0]);
  const curlOpacity = useTransform(tearProgress, [0, 0.03, 0.9, 1], [0, 1, 1, 0]);

  const sealShown = useTransform(
    [sealOpacity, tearProgress],
    ([s, p]) => s * (1 - clamp((p - 0.4) / 0.2, 0, 1))
  );
  const sealScale = useTransform(
    [tearProgress, sealPop],
    ([p, pop]) => (1 - 0.25 * clamp((p - 0.4) / 0.2, 0, 1)) * pop
  );
  const flapOuterOpacity = useTransform(flapRotate, [89, 91], [1, 0]);
  const flapInnerOpacity = useTransform(flapRotate, [89, 91], [0, 1]);
  const flapOuterShade = useTransform(flapRotate, [0, 45, 90], [0, 0.1, 0.26]);
  const flapInnerShade = useTransform(flapRotate, [90, 130, 180], [0.3, 0.1, 0]);
  const flapShadowOpacity = useTransform(flapRotate, [0, 14, 60, 100], [0.55, 0.8, 0.45, 0]);
  const flapShadowScale = useTransform(flapRotate, [0, 14, 70], [1, 1.04, 1.22]);
  const flapShadowY = useTransform(flapRotate, [0, 14, 70], [1.5, 4, 12]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const geometry = useMemo(() => {
    const { width, height } = size;
    if (!width || !height) return null;
    const cutY = Math.round(clamp(height * 0.035, 7, 14));
    const flapDepth = height * (isPortrait ? 0.42 : 0.52);
    const keyLength = Math.round(clamp(width * 0.4, 132, 196));
    return {
      width,
      height,
      cutY,
      flapDepth,
      notchDepth: flapDepth * notchShare,
      keyLength,
      keyHeight: (keyLength * keyViewHeight) / keyViewWidth,
      tear: buildTearGeometry(width, cutY),
      perspective: Math.round(Math.max(1100, height * 3.4)),
    };
  }, [size, isPortrait]);
  geometryRef.current = geometry;

  const durationFor = useCallback((full) => (prefersReducedMotion ? Math.min(full, 0.35) : full), [prefersReducedMotion]);

  const handleFlip = () => {
    if (transitioningRef.current) return;
    if (phase === "back") setState((s) => ({ ...s, phase: "front" }));
  };

  const spawnFlecks = (progress) => {
    const layer = fleckLayerRef.current;
    const current = geometryRef.current;
    if (!layer || !current || prefersReducedMotion || typeof layer.animate !== "function") return;
    if (layer.childElementCount > 36) return;
    const originX = progress * current.width;
    const originY = current.cutY;
    const count = 1 + Math.floor(Math.random() * 2.4);
    for (let index = 0; index < count; index += 1) {
      const fleck = document.createElement("span");
      const isFiber = Math.random() < 0.4;
      const width = isFiber ? 4 + Math.random() * 4 : 1.4 + Math.random() * 2.2;
      const height = isFiber ? 0.8 : width * (0.6 + Math.random() * 0.5);
      fleck.className = "envelope-fleck";
      fleck.style.width = `${width.toFixed(1)}px`;
      fleck.style.height = `${height.toFixed(1)}px`;
      fleck.style.background = fleckColors[Math.floor(Math.random() * fleckColors.length)];
      fleck.style.left = `${(originX - width / 2).toFixed(1)}px`;
      fleck.style.top = `${(originY - height / 2).toFixed(1)}px`;
      layer.appendChild(fleck);
      const driftX = -6 - Math.random() * 22;
      const liftY = -6 - Math.random() * 16;
      const fallY = liftY + 14 + Math.random() * 22;
      const spin = (Math.random() - 0.5) * 540;
      const animation = fleck.animate(
        [
          { transform: "translate3d(0, 0, 0) rotate(0deg)", opacity: 1 },
          { transform: `translate3d(${(driftX * 0.55).toFixed(1)}px, ${liftY.toFixed(1)}px, 0) rotate(${(spin * 0.4).toFixed(0)}deg)`, opacity: 0.95, offset: 0.35 },
          { transform: `translate3d(${driftX.toFixed(1)}px, ${fallY.toFixed(1)}px, 0) rotate(${spin.toFixed(0)}deg)`, opacity: 0 },
        ],
        { duration: 650 + Math.random() * 500, easing: "cubic-bezier(0.2, 0.7, 0.4, 1)" }
      );
      animation.onfinish = () => fleck.remove();
    }
  };

  const beginOpen = () => {
    if (phase !== "front" || transitioningRef.current) return;
    transitioningRef.current = true;
    setIsCutting(false);
    setState((s) => ({ ...s, phase: "opening" }));
    keyLean.set(0);
    animate(tearProgress, 1, { duration: 0.22, ease: "easeOut" });
    animate(keyExit, 1, { duration: durationFor(0.6), ease: [0.4, 0, 0.2, 1], delay: 0.1 });
    animate(sealOpacity, 0, { duration: 0.3 });
    const flapStart = flapRotate.get();
    animate(
      flapRotate,
      prefersReducedMotion ? openAngle : [flapStart, openAngle + 5, openAngle],
      {
        duration: durationFor(1.05),
        delay: prefersReducedMotion ? 0 : 0.16,
        times: prefersReducedMotion ? undefined : [0, 0.8, 1],
        ease: prefersReducedMotion ? "easeInOut" : [[0.5, 0, 0.3, 1], [0.4, 0, 0.6, 1]],
        onComplete: () => {
          setState((s) => ({ ...s, phase: "emerging", showEmerge: true }));
        },
      }
    );
  };

  const handleTearStart = () => {
    strokeStartRef.current = tearProgress.get();
    lastFleckProgressRef.current = tearProgress.get();
  };

  const handleDragStart = () => {
    if (phase === "front" && !transitioningRef.current) setIsCutting(true);
  };

  const handleTear = (event, info) => {
    if (phase !== "front" || transitioningRef.current) return;
    const width = geometryRef.current ? geometryRef.current.width : 300;
    const next = clamp(strokeStartRef.current + info.offset.x / width, 0, 1);
    if (next > tearProgress.get()) {
      tearProgress.set(next);
      flapRotate.set(next * tearLiftAngle);
      if ((next - lastFleckProgressRef.current) * width > 9) {
        lastFleckProgressRef.current = next;
        spawnFlecks(next);
      }
    }
    keyLean.set(clamp(info.velocity.x / 110, -4, 10));
    if (tearProgress.get() >= 0.92) beginOpen();
  };

  const handleTearEnd = () => {
    setIsCutting(false);
    keyLean.set(0);
    if (transitioningRef.current) return;
    if (tearProgress.get() >= 0.6) beginOpen();
  };

  const playSettle = useCallback((direction, delay) => {
    if (prefersReducedMotion) return;
    const isRelease = direction === "release";
    animate(settleScaleY, isRelease ? [1, 1.016, 0.994, 1] : [1, 0.972, 1.008, 1], {
      duration: 0.62,
      delay,
      times: [0, 0.3, 0.68, 1],
      ease: "easeOut",
    });
    animate(settleScaleX, isRelease ? [1, 0.992, 1.003, 1] : [1, 1.012, 0.997, 1], {
      duration: 0.62,
      delay,
      times: [0, 0.3, 0.68, 1],
      ease: "easeOut",
    });
  }, [prefersReducedMotion, settleScaleX, settleScaleY]);

  useLayoutEffect(() => {
    if (phase !== "emerging" || !showEmerge) return undefined;
    const paperEl = emergePaperRef.current;
    const wrapEl = wrapRef.current;
    const current = geometryRef.current;
    if (!paperEl || !wrapEl || !current) return undefined;

    const envelopeTop = wrapEl.getBoundingClientRect().top - sinkY.get();
    const visualHeight = paperEl.offsetHeight * emergeScale;
    const hiddenY = current.notchDepth + 8;
    const outY = -(visualHeight + 12);
    const neededSink = viewportMargin - (envelopeTop + outY);
    const maxSink = Math.max(0, window.innerHeight - envelopeTop - current.height * 0.42);
    const sink = clamp(neededSink, 0, maxSink);
    const overshoot = prefersReducedMotion ? 0 : Math.min(22, visualHeight * 0.05);
    const riseDuration = durationFor(1.25);

    emergeY.set(hiddenY);
    const sinkControls = animate(sinkY, sink, { duration: riseDuration, ease: [0.45, 0, 0.2, 1], delay: 0.08 });
    playSettle("release", 0.08 + riseDuration * 0.58);
    const riseControls = animate(
      emergeY,
      prefersReducedMotion ? outY : [hiddenY, outY - overshoot, outY],
      {
        duration: riseDuration,
        delay: 0.08,
        times: prefersReducedMotion ? undefined : [0, 0.76, 1],
        ease: prefersReducedMotion ? "easeOut" : [[0.42, 0, 0.22, 1], [0.45, 0, 0.55, 1]],
        onComplete: () => {
          emergeEndRectRef.current = paperEl.getBoundingClientRect();
          setState((s) => ({ ...s, showEmerge: false, showReading: true, phase: "reading" }));
        },
      }
    );
    return () => {
      riseControls.stop();
      sinkControls.stop();
    };
  }, [phase, showEmerge, emergeY, sinkY, prefersReducedMotion, durationFor, playSettle]);

  useLayoutEffect(() => {
    if (phase !== "resealing" || !showEmerge) return undefined;
    const current = geometryRef.current;
    if (!emergePaperRef.current || !current) return undefined;

    const startY = emergeY.get();
    const hiddenY = current.notchDepth + 8;
    const dropDuration = durationFor(1.0);
    const sinkControls = animate(sinkY, 0, { duration: dropDuration, ease: [0.4, 0, 0.2, 1] });
    playSettle("press", dropDuration * 0.78);
    const dropControls = animate(
      emergeY,
      prefersReducedMotion ? hiddenY : [startY, startY - 14, hiddenY],
      {
        duration: dropDuration,
        times: prefersReducedMotion ? undefined : [0, 0.22, 1],
        ease: prefersReducedMotion ? "easeIn" : [[0.3, 0, 0.5, 1], [0.55, 0, 0.8, 0.7]],
        onComplete: () => {
          tearProgress.set(0);
          animate(sealOpacity, 1, { duration: 0.35, delay: durationFor(0.75) });
          if (!prefersReducedMotion) {
            animate(sealPop, [0.6, 1.14, 1], { duration: 0.5, delay: 0.75, times: [0, 0.55, 1], ease: "easeOut" });
          }
          animate(
            flapRotate,
            prefersReducedMotion ? 0 : [openAngle, 0, 5, 0],
            {
              duration: durationFor(1.0),
              delay: prefersReducedMotion ? 0 : 0.1,
              times: prefersReducedMotion ? undefined : [0, 0.74, 0.87, 1],
              ease: prefersReducedMotion ? "easeInOut" : [[0.5, 0, 0.75, 0.6], "easeOut", "easeIn"],
              onComplete: () => {
                setState((s) => ({ ...s, showEmerge: false, phase: "front" }));
                transitioningRef.current = false;
                animate(keyExit, 0, { duration: durationFor(0.65), ease: [0.2, 0.7, 0.3, 1], delay: 0.1 });
              },
            }
          );
        },
      }
    );
    return () => {
      dropControls.stop();
      sinkControls.stop();
    };
  }, [phase, showEmerge, emergeY, sinkY, sealOpacity, sealPop, flapRotate, tearProgress, keyExit, prefersReducedMotion, durationFor, playSettle]);

  useLayoutEffect(() => {
    if (!showReading) return;
    const el = readingPaperRef.current;
    const emergeRect = emergeEndRectRef.current;
    if (!el || !emergeRect) {
      transitioningRef.current = false;
      return;
    }
    const naturalWidth = el.offsetWidth;
    const naturalHeight = el.offsetHeight;
    const pose = {
      x: emergeRect.left + emergeRect.width / 2 - window.innerWidth / 2,
      y: emergeRect.top + emergeRect.height / 2 - window.innerHeight / 2,
      scaleX: emergeRect.width / naturalWidth,
      scaleY: emergeRect.height / naturalHeight,
    };
    poseRef.current = pose;
    readingX.set(pose.x);
    readingY.set(pose.y);
    readingScaleX.set(pose.scaleX);
    readingScaleY.set(pose.scaleY);
    readingTilt.set(0);
    const travel = durationFor(1.05);
    const glide = { duration: travel, ease: [0.45, 0, 0.2, 1], delay: prefersReducedMotion ? 0 : 0.12 };
    animate(readingX, 0, glide);
    animate(readingScaleX, 1, glide);
    animate(readingScaleY, 1, glide);
    if (!prefersReducedMotion) {
      animate(readingTilt, [0, 10, 0], { duration: travel, delay: 0.12, times: [0, 0.45, 1], ease: "easeInOut" });
    }
    animate(
      readingY,
      prefersReducedMotion ? 0 : [pose.y, pose.y - 10, 7, 0],
      {
        duration: prefersReducedMotion ? travel : travel + 0.12,
        times: prefersReducedMotion ? undefined : [0, 0.2, 0.84, 1],
        ease: prefersReducedMotion ? "easeInOut" : ["easeOut", [0.5, 0, 0.3, 1], "easeInOut"],
        onComplete: () => {
          transitioningRef.current = false;
        },
      }
    );
  }, [showReading, readingX, readingY, readingScaleX, readingScaleY, readingTilt, prefersReducedMotion, durationFor]);

  const beginReseal = () => {
    if (phase !== "reading" || transitioningRef.current) return;
    transitioningRef.current = true;
    setState((s) => ({ ...s, phase: "resealing" }));
    const pose = poseRef.current || {
      x: 0,
      y: -0.22 * window.innerHeight,
      scaleX: 0.86,
      scaleY: 0.86,
    };
    const travel = durationFor(0.95);
    const glide = { duration: travel, ease: [0.45, 0, 0.25, 1] };
    animate(readingX, pose.x, glide);
    animate(readingScaleX, pose.scaleX, glide);
    animate(readingScaleY, pose.scaleY, glide);
    if (!prefersReducedMotion) {
      animate(readingTilt, [0, 10, 0], { duration: travel, times: [0, 0.55, 1], ease: "easeInOut" });
    }
    animate(
      readingY,
      prefersReducedMotion ? pose.y : [0, pose.y - 16, pose.y],
      {
        duration: travel,
        times: prefersReducedMotion ? undefined : [0, 0.8, 1],
        ease: prefersReducedMotion ? "easeInOut" : [[0.45, 0, 0.3, 1], [0.4, 0, 0.6, 1]],
        onComplete: () => {
          setState((s) => ({ ...s, showEmerge: true, showReading: false }));
        },
      }
    );
  };

  const flipTransition = {
    duration: prefersReducedMotion ? 0.4 : 0.95,
    ease: [0.6, 0, 0.3, 1],
    rotateY: prefersReducedMotion
      ? { duration: 0.4, ease: "easeInOut" }
      : { duration: 0.95, times: [0, 0.8, 1], ease: [[0.6, 0, 0.3, 1], [0.4, 0, 0.6, 1]] },
  };
  const flipVariants = {
    back: {
      rotateY: prefersReducedMotion ? 0 : [null, -5, 0],
      scale: prefersReducedMotion ? 1 : [null, 1.06, 1],
      y: prefersReducedMotion ? 0 : [null, -14, 0],
      transition: flipTransition,
    },
    front: {
      rotateY: prefersReducedMotion ? 180 : [null, 185, 180],
      scale: prefersReducedMotion ? 1 : [null, 1.06, 1],
      y: prefersReducedMotion ? 0 : [null, -14, 0],
      transition: flipTransition,
    },
  };

  const showPrompt = phase === "back" || (phase === "front" && frontArmed);
  const keyHinting = phase === "front" && frontArmed && !isCutting && !prefersReducedMotion;
  const seamLift = geometry ? Math.round(geometry.keyLength * 0.62 + 12) : 0;
  const emergeClipPath = geometry
    ? `polygon(0 0, 100% 0, 100% ${geometry.height * emergeClipLead}px, 50% ${(geometry.height * emergeClipLead + geometry.notchDepth).toFixed(1)}px, 0 ${geometry.height * emergeClipLead}px)`
    : "none";
  const notchPolygon = geometry
    ? `polygon(0 0, 50% ${geometry.notchDepth.toFixed(1)}px, 100% 0, 100% 100%, 0 100%)`
    : "polygon(0 0, 50% 40%, 100% 0, 100% 100%, 0 100%)";

  return (
    <div style={styles.column}>
      <motion.div
        style={{ ...styles.perspective, ...(isPortrait ? portraitStyles.perspective : null), y: sinkY }}
        ref={wrapRef}
      >
        <motion.div style={{ ...styles.settleLayer, scaleX: settleScaleX, scaleY: settleScaleY }}>
          <motion.div
            style={styles.flipInner}
            initial={false}
            variants={flipVariants}
            animate={phase === "back" ? "back" : "front"}
            onAnimationComplete={(definition) => {
              if (definition === "front") setState((s) => (s.phase === "front" ? { ...s, frontArmed: true } : s));
            }}
          >
            <div style={{ ...styles.face, ...styles.backFace }} onClick={handleFlip}>
              <img src={resolvedBackImage} alt="" style={styles.backImg} />
              <svg viewBox={fold.viewBox} preserveAspectRatio="none" style={styles.backFolds} aria-hidden="true">
                <path d={fold.topPath} fill="none" stroke="rgba(90,58,68,0.16)" strokeWidth="0.5" />
                <path d={fold.topPath} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.3" />
                <path d={fold.bottomPath} fill="none" stroke="rgba(90,58,68,0.1)" strokeWidth="0.4" />
                <path d={fold.bottomPath} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.25" />
                <rect x={fold.rect.x} y={fold.rect.y} width={fold.rect.width} height={fold.rect.height} fill="none" stroke="rgba(90,58,68,0.12)" strokeWidth="0.6" />
              </svg>
            </div>

            <div style={{ ...styles.face, ...styles.frontFace }}>
              <div style={{ ...styles.body, perspective: geometry ? geometry.perspective : 1400 }}>
                <div style={styles.interior} />
                <div style={{ ...styles.pocketFront, clipPath: notchPolygon, WebkitClipPath: notchPolygon }}>
                  <div style={styles.foldLeft} />
                  <div style={styles.foldRight} />
                  <div style={styles.foldBottom} />
                </div>
                {geometry && (
                  <svg
                    viewBox={`0 0 ${geometry.width} ${geometry.height}`}
                    style={styles.fullLayer}
                    aria-hidden="true"
                  >
                    <path
                      d={`M0 0 L${geometry.width / 2} ${geometry.notchDepth} L${geometry.width} 0`}
                      fill="none"
                      stroke="rgba(150,78,102,0.28)"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                    <path
                      d={`M0 1.2 L${geometry.width / 2} ${geometry.notchDepth + 1.2} L${geometry.width} 1.2`}
                      fill="none"
                      stroke="rgba(255,255,255,0.55)"
                      strokeWidth="0.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <motion.div
                  style={{
                    ...styles.flapShadow,
                    ...(isPortrait ? portraitStyles.flapSized : null),
                    opacity: flapShadowOpacity,
                    scaleY: flapShadowScale,
                    y: flapShadowY,
                  }}
                />
                <motion.div
                  style={{
                    ...styles.flap,
                    ...(isPortrait ? portraitStyles.flapSized : null),
                    rotateX: flapRotate,
                  }}
                >
                  <motion.div style={{ ...styles.flapFace, ...styles.flapOuter, opacity: flapOuterOpacity }}>
                    <motion.div style={{ ...styles.flapShade, opacity: flapOuterShade }} />
                    <motion.div style={{ ...styles.seal, opacity: sealShown, scale: sealScale }} />
                  </motion.div>
                  <motion.div style={{ ...styles.flapFace, ...styles.flapInner, opacity: flapInnerOpacity }}>
                    <motion.div style={{ ...styles.flapShade, opacity: flapInnerShade }} />
                  </motion.div>
                </motion.div>
              </div>

              {geometry && (
                <motion.div style={{ ...styles.tearLayer, height: geometry.cutY + 8, opacity: tearOverlayOpacity }}>
                  <svg
                    viewBox={`0 0 ${geometry.width} ${geometry.cutY + 8}`}
                    width={geometry.width}
                    height={geometry.cutY + 8}
                    style={styles.tearSvg}
                    aria-hidden="true"
                  >
                    <line x1="0" x2={geometry.width} y1={geometry.cutY - 0.6} y2={geometry.cutY - 0.6} stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
                    <line x1="0" x2={geometry.width} y1={geometry.cutY + 0.4} y2={geometry.cutY + 0.4} stroke="rgba(176,98,124,0.16)" strokeWidth="0.8" strokeDasharray="3 2.2" />
                  </svg>
                  <motion.svg
                    viewBox={`0 0 ${geometry.width} ${geometry.cutY + 8}`}
                    width={geometry.width}
                    height={geometry.cutY + 8}
                    style={{ ...styles.tearSvg, clipPath: tearClip, WebkitClipPath: tearClip }}
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="envelope-strip-lift" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
                        <stop offset="1" stopColor="#ffffff" stopOpacity="0.45" />
                      </linearGradient>
                    </defs>
                    <path d={geometry.tear.strip} fill="url(#envelope-strip-lift)" />
                    <path d={geometry.tear.shadow} fill="none" stroke="rgba(150,70,95,0.16)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d={geometry.tear.line} fill="none" stroke="#a9607a" strokeOpacity="0.62" strokeWidth="2.1" strokeLinejoin="round" />
                    <path d={geometry.tear.upper} fill="none" stroke="#fffaf6" strokeWidth="1.1" strokeLinejoin="round" />
                    <path d={geometry.tear.lower} fill="none" stroke="#fffaf6" strokeWidth="1.3" strokeLinejoin="round" />
                    <path d={geometry.tear.fibers} fill="none" stroke="#fffaf6" strokeOpacity="0.9" strokeWidth="0.55" strokeLinecap="round" />
                  </motion.svg>
                </motion.div>
              )}

              <div ref={fleckLayerRef} style={styles.fleckLayer} aria-hidden="true" />

              {geometry && (
                <div style={styles.keyTrack} aria-hidden="true">
                  <motion.div style={{ ...styles.keyCarriage, x: keyCarriageX }}>
                    <div style={{ ...styles.keyAnchor, top: geometry.cutY }}>
                      <motion.div style={{ ...styles.paperCurl, opacity: curlOpacity }} />
                      <motion.div
                        style={{
                          ...styles.contactShadow,
                          opacity: keyOpacity,
                          scale: isCutting ? 0.8 : 1,
                        }}
                      />
                      <motion.div
                        style={{
                          ...styles.keyPivot,
                          x: keyExitX,
                          y: keyExitY,
                          rotate: keyExitRotate,
                          opacity: keyOpacity,
                        }}
                      >
                        <motion.div style={{ ...styles.keyPivot, rotate: keyAngle }}>
                          <div className={keyHinting ? "letter-key-hint" : undefined} style={styles.keyPivot}>
                            <LetterKey length={geometry.keyLength} />
                          </div>
                        </motion.div>
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              )}

              <motion.div
                className="tear-seam"
                style={{
                  ...styles.seam,
                  top: -seamLift,
                  height: `calc(56% + ${seamLift}px)`,
                  cursor: phase === "front" && frontArmed ? (isCutting ? "grabbing" : "grab") : "default",
                  pointerEvents: phase === "front" ? "auto" : "none",
                }}
                drag={phase === "front" ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0}
                dragMomentum={false}
                onPointerDown={handleTearStart}
                onDragStart={handleDragStart}
                onDrag={handleTear}
                onDragEnd={handleTearEnd}
              />
            </div>
          </motion.div>
        </motion.div>

        {showEmerge && (
          <div style={{ ...styles.emergeClip, clipPath: emergeClipPath, WebkitClipPath: emergeClipPath }}>
            <motion.div
              ref={emergePaperRef}
              style={{
                ...styles.emergePaper,
                ...(isPortrait ? portraitStyles.emergePaper : null),
                y: emergeY,
              }}
            >
              <Letter text={letterText} showReseal={false} />
            </motion.div>
          </div>
        )}
      </motion.div>

      <div style={{ ...styles.prompt, opacity: showPrompt ? 1 : 0 }}>tap for more my love</div>

      {(showReading || phase === "resealing") && (
        <div style={styles.readingLayer}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "reading" ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            style={styles.readingBackdrop}
          />
          {showReading && (
            <motion.div
              ref={readingPaperRef}
              style={{
                ...styles.readingPaper,
                ...(isPortrait ? portraitStyles.readingPaper : null),
                x: readingX,
                y: readingY,
                scaleX: readingScaleX,
                scaleY: readingScaleY,
                rotateX: readingTilt,
                transformPerspective: 1400,
              }}
            >
              <Letter text={letterText} onReseal={beginReseal} showReseal={phase === "reading"} />
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}

const flapClip = "polygon(0 0, 100% 0, 50% 100%)";

const styles = {
  column: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.75rem",
  },
  perspective: {
    position: "relative",
    width: "min(88vw, calc(66dvh * 11 / 8.5))",
    aspectRatio: "11 / 8.5",
  },
  settleLayer: {
    position: "absolute",
    inset: 0,
    perspective: 1400,
    originY: 1,
  },
  flipInner: {
    position: "relative",
    width: "100%",
    height: "100%",
    transformStyle: "preserve-3d",
    zIndex: 1,
  },
  face: {
    position: "absolute",
    inset: 0,
    borderRadius: 2,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    boxShadow: "0 16px 44px rgba(217,138,163,0.24)",
  },
  backFace: {
    cursor: "pointer",
    overflow: "hidden",
  },
  backImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  backFolds: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
  frontFace: {
    transform: "rotateY(180deg)",
  },
  body: {
    position: "relative",
    width: "100%",
    height: "100%",
    perspectiveOrigin: "50% 20%",
  },
  interior: {
    position: "absolute",
    inset: 0,
    borderRadius: 2,
    background: "linear-gradient(180deg, #c98a9c 0%, #dda0b1 24%, #ecb9c6 55%, #f4cad4 100%)",
  },
  pocketFront: {
    position: "absolute",
    inset: 0,
    background: "#f9d5dd",
    borderRadius: 2,
    overflow: "hidden",
  },
  fullLayer: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    overflow: "visible",
    pointerEvents: "none",
  },
  foldLeft: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "50%",
    height: "100%",
    background: "linear-gradient(135deg, rgba(244,182,194,0.7), transparent 60%)",
    clipPath: "polygon(0 0, 100% 50%, 0 100%)",
  },
  foldRight: {
    position: "absolute",
    right: 0,
    top: 0,
    width: "50%",
    height: "100%",
    background: "linear-gradient(225deg, rgba(232,160,180,0.7), transparent 60%)",
    clipPath: "polygon(100% 0, 0 50%, 100% 100%)",
  },
  foldBottom: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
    height: "50%",
    background: "linear-gradient(0deg, rgba(255,255,255,0.22), transparent 70%)",
    clipPath: "polygon(0 100%, 50% 0, 100% 100%)",
  },
  flapShadow: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "52%",
    originY: 0,
    background: "linear-gradient(180deg, rgba(150,70,96,0.05), rgba(150,70,96,0.3))",
    clipPath: flapClip,
    WebkitClipPath: flapClip,
    pointerEvents: "none",
  },
  flap: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "52%",
    originY: 0,
    transformStyle: "flat",
  },
  flapFace: {
    position: "absolute",
    inset: 0,
    clipPath: flapClip,
    WebkitClipPath: flapClip,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  flapOuter: {
    background: "linear-gradient(180deg, #f6c0cb 0%, #f4b6c2 60%, #eeaab8 100%)",
  },
  flapInner: {
    background: "linear-gradient(0deg, #e6aab9 0%, #efbcc8 60%, #f2c3ce 100%)",
  },
  flapShade: {
    position: "absolute",
    inset: 0,
    background: "#7d3a52",
    pointerEvents: "none",
  },
  seal: {
    position: "relative",
    width: 30,
    height: 30,
    background: "radial-gradient(circle at 38% 35%, #e8a0b4 0%, #d98aa3 55%, #c97892 100%)",
    WebkitMaskImage: heartMask,
    maskImage: heartMask,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    marginTop: "-10%",
  },
  tearLayer: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    pointerEvents: "none",
    zIndex: 3,
  },
  tearSvg: {
    position: "absolute",
    left: 0,
    top: 0,
    display: "block",
    overflow: "visible",
  },
  fleckLayer: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 5,
  },
  keyTrack: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: 0,
    pointerEvents: "none",
    zIndex: 6,
  },
  keyCarriage: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: 0,
  },
  keyAnchor: {
    position: "absolute",
    left: 0,
    width: 0,
    height: 0,
  },
  keyPivot: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    originX: 0,
    originY: 0,
    transformOrigin: "0 0",
  },
  paperCurl: {
    position: "absolute",
    right: -1,
    top: -4,
    width: 34,
    height: 7,
    borderRadius: "60% 40% 40% 60% / 70% 70% 30% 30%",
    background: "linear-gradient(90deg, rgba(255,250,246,0) 0%, rgba(255,250,246,0.75) 70%, rgba(255,255,255,0.95) 100%)",
    boxShadow: "0 2px 2px rgba(150,70,95,0.14)",
  },
  contactShadow: {
    position: "absolute",
    left: -3,
    top: -2,
    width: 24,
    height: 7,
    borderRadius: "50%",
    background: "radial-gradient(closest-side, rgba(110,50,60,0.26), rgba(110,50,60,0))",
  },
  seam: {
    position: "absolute",
    left: 0,
    width: "100%",
    touchAction: "none",
    zIndex: 7,
    background: "transparent",
  },
  prompt: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: "italic",
    fontSize: "clamp(1.2rem, 0.9rem + 0.6vw, 1.55rem)",
    letterSpacing: "0.02em",
    color: "#b86a84",
    minHeight: "1.6em",
    lineHeight: 1.3,
    transition: "opacity 0.4s ease",
  },
  emergeClip: {
    position: "absolute",
    left: 0,
    top: `${-emergeClipLead * 100}%`,
    width: "100%",
    height: `${(emergeClipLead + 1) * 100}%`,
    zIndex: 2,
    pointerEvents: "none",
  },
  emergePaper: {
    position: "absolute",
    left: "6%",
    top: `${(emergeClipLead / (emergeClipLead + 1)) * 100}%`,
    width: "88%",
    aspectRatio: "11 / 8.5",
    scale: emergeScale,
    originY: 0,
    pointerEvents: "none",
  },
  readingLayer: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  readingBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(251,228,234,0.6)",
    pointerEvents: "none",
  },
  readingPaper: {
    position: "relative",
    width: "min(960px, 92vw, calc(88dvh * 11 / 8.5))",
    maxWidth: "92vw",
    height: "min(88dvh, max(70dvh, calc(92vw * 8.5 / 11)))",
    maxHeight: "88dvh",
    pointerEvents: "auto",
  },
};

const portraitStyles = {
  perspective: {
    width: "min(88vw, calc(70dvh * 8.5 / 11))",
    aspectRatio: "8.5 / 11",
  },
  flapSized: {
    height: "42%",
  },
  emergePaper: {
    aspectRatio: "8.5 / 11",
  },
  readingPaper: {
    position: "relative",
    width: "min(560px, 92vw)",
    height: "min(88dvh, calc(min(560px, 92vw) * 11 / 8.5))",
    maxHeight: "88dvh",
  },
};
